import { useCallback, useEffect, useRef, useState } from 'react';
import type { TFunction } from 'i18next';
import { calculateOrderItemPricing, type OrderItemPricingRequest } from '@/services/pricingService';
import { logging } from '@/services/logging';

type PreviewPriceState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'failed' }
  | { status: 'success'; value: number; currency: string };

type PreviewRequest =
  | { type: 'reset' }
  | {
      type: 'preview';
      payload: OrderItemPricingRequest;
    };

interface UseDebouncedPricingPreviewOptions {
  readonly debounceMs?: number;
}

const signatureForPayload = (payload: OrderItemPricingRequest): string => {
  const parts: Array<string | number | null | undefined> = [
    payload.uploadedFileId,
    payload.widthInches,
    payload.heightInches,
    payload.galleryWrapInches,
    payload.paperTypeId,
    payload.printerId,
  ];
  return parts.map(part => (part ?? '')).join('|');
};

const useDebouncedPricingPreview = (
  options?: UseDebouncedPricingPreviewOptions,
): {
  previewPrice: PreviewPriceState;
  requestPreview: (request: PreviewRequest) => void;
} => {
  const debounceMs = options?.debounceMs ?? 600;
  const [previewPrice, setPreviewPrice] = useState<PreviewPriceState>({ status: 'idle' });
  const timeoutRef = useRef<number | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const lastSuccessfulSignatureRef = useRef<string | null>(null);
  const pendingSignatureRef = useRef<string | null>(null);

  const clearTimer = useCallback(() => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const abortInFlight = useCallback(() => {
    controllerRef.current?.abort();
    controllerRef.current = null;
  }, []);

  const resetState = useCallback(() => {
    clearTimer();
    abortInFlight();
    lastSuccessfulSignatureRef.current = null;
    pendingSignatureRef.current = null;
    setPreviewPrice({ status: 'idle' });
  }, [abortInFlight, clearTimer]);

  const requestPreview = useCallback(
    (request: PreviewRequest) => {
      if (request.type === 'reset') {
        resetState();
        return;
      }

      const payload = request.payload;
      if (!payload?.uploadedFileId) {
        resetState();
        return;
      }

      if (
        typeof payload.widthInches !== 'number'
        || payload.widthInches <= 0
        || typeof payload.heightInches !== 'number'
        || payload.heightInches <= 0
      ) {
        resetState();
        return;
      }

      const signature = signatureForPayload(payload);

      if (lastSuccessfulSignatureRef.current === signature && previewPrice.status === 'success') {
        return;
      }

      if (pendingSignatureRef.current === signature) {
        return;
      }

      pendingSignatureRef.current = signature;
      clearTimer();
      setPreviewPrice({ status: 'loading' });

      timeoutRef.current = window.setTimeout(async () => {
        abortInFlight();
        const controller = new AbortController();
        controllerRef.current = controller;

        try {
          const result = await calculateOrderItemPricing(payload, { signal: controller.signal });
          lastSuccessfulSignatureRef.current = signature;
          pendingSignatureRef.current = null;
          setPreviewPrice({
            status: 'success',
            value: result.price.value,
            currency: result.price.currency,
          });
        } catch (error) {
          if (controller.signal.aborted) {
            return;
          }
          logging.error('[useDebouncedPricingPreview] Failed to fetch preview price', { error });
          pendingSignatureRef.current = null;
          lastSuccessfulSignatureRef.current = null;
          setPreviewPrice({ status: 'failed' });
        } finally {
          controllerRef.current = null;
          timeoutRef.current = null;
        }
      }, debounceMs);
    },
    [abortInFlight, clearTimer, debounceMs, previewPrice.status, resetState],
  );

  useEffect(() => {
    return () => {
      clearTimer();
      abortInFlight();
    };
  }, [abortInFlight, clearTimer]);

  return {
    previewPrice,
    requestPreview,
  };
};

export type { PreviewPriceState, PreviewRequest };
interface PricingFallback {
  price?: {
    value: number;
    currency: string;
  } | null;
  pricingStatus?: string | null;
}

const formatCurrency = (value: number, currency: string): string => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
    }).format(value);
  } catch {
    return `${value}`;
  }
};

interface PricingPreviewLabelOptions {
  readonly previewPrice: PreviewPriceState;
  readonly fallback?: PricingFallback | null;
  readonly t: TFunction;
}

export const getPricingPreviewLabel = ({ previewPrice, fallback, t }: PricingPreviewLabelOptions): string => {
  if (previewPrice.status === 'loading') {
    return t('pricingPreview.loading', 'Calculating…');
  }

  if (previewPrice.status === 'failed') {
    return t('pricingPreview.error', 'Unavailable');
  }

  if (previewPrice.status === 'success') {
    return formatCurrency(previewPrice.value, previewPrice.currency);
  }

  const status = fallback?.pricingStatus;
  if (status === 'loading') {
    return t('pricingPreview.loading', 'Calculating…');
  }

  if (status === 'failed') {
    return t('pricingPreview.error', 'Unavailable');
  }

  const fallbackPrice = fallback?.price;
  if (fallbackPrice && typeof fallbackPrice.value === 'number' && fallbackPrice.currency) {
    return formatCurrency(fallbackPrice.value, fallbackPrice.currency);
  }

  return t('pricingPreview.missing', 'TBD');
};

type PricingAwareEntity = {
  price?: {
    value: number;
    currency: string;
  } | null;
  pricingStatus?: string | null;
} | null | undefined;

interface PricingLabelFromItemOptions {
  readonly item: PricingAwareEntity;
  readonly t: TFunction;
}

export const getStoredPricingLabel = ({ item, t }: PricingLabelFromItemOptions): string | null => {
  if (!item) {
    return null;
  }

  const fallback: PricingFallback = {
    price: item.price ?? null,
    pricingStatus: item.pricingStatus ?? null,
  };

  if (!fallback.price && !fallback.pricingStatus) {
    return null;
  }

  return getPricingPreviewLabel({ previewPrice: { status: 'idle' }, fallback, t });
};

export default useDebouncedPricingPreview;
